import { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  FormControlLabel,
  Checkbox,
  FormGroup,
  Typography,
  Alert,
  Grid2 as Grid,
  Divider,
  Chip,
} from '@mui/material';
import { Delete as DeleteIcon } from '@mui/icons-material';
import TimeField from './ui/TimeField';
import { SHIFT_DEFAULTS } from '../utils/constants';

export default function ShiftForm({ open, onClose, onSubmit, onDelete, shift, scheduleId, availablePlaceServices = [] }) {
  const [formData, setFormData] = useState({
    scheduleId: scheduleId || '',
    monday: false,
    tuesday: false,
    wednesday: false,
    thursday: false,
    friday: false,
    saturday: false,
    sunday: false,
    startTime: SHIFT_DEFAULTS.START_TIME,
    endTime: SHIFT_DEFAULTS.END_TIME,
    breakBetweenAppointmentsMinutes: SHIFT_DEFAULTS.BREAK_BETWEEN_APPOINTMENTS,
    isActive: true,
    serviceIds: [], // Backend espera serviceIds (ServiceId), não placeServiceIds
  });

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (shift) {
      // Edição - preenche com dados do turno existente
      setFormData({
        scheduleId: shift.scheduleId || scheduleId,
        monday: shift.monday || false,
        tuesday: shift.tuesday || false,
        wednesday: shift.wednesday || false,
        thursday: shift.thursday || false,
        friday: shift.friday || false,
        saturday: shift.saturday || false,
        sunday: shift.sunday || false,
        startTime: shift.startTime ? formatTimeSpan(shift.startTime) : '09:00',
        endTime: shift.endTime ? formatTimeSpan(shift.endTime) : '18:00',
        breakBetweenAppointmentsMinutes: shift.breakBetweenAppointmentsMinutes || 0,
        isActive: shift.isActive ?? true,
        // Backend retorna shift.services com serviceId
        serviceIds: shift.services?.map(s => s.serviceId) || [],
      });
    } else {
      // Novo turno - valores padrão
      setFormData({
        scheduleId: scheduleId || '',
        monday: false,
        tuesday: false,
        wednesday: false,
        thursday: false,
        friday: false,
        saturday: false,
        sunday: false,
        startTime: SHIFT_DEFAULTS.START_TIME,
        endTime: SHIFT_DEFAULTS.END_TIME,
        breakBetweenAppointmentsMinutes: SHIFT_DEFAULTS.BREAK_BETWEEN_APPOINTMENTS,
        isActive: true,
        serviceIds: [],
      });
    }
    setErrors({});
  }, [shift, scheduleId, open]);

  // Formata TimeSpan do C# (HH:mm:ss) para input time (HH:mm)
  const formatTimeSpan = (timeSpan) => {
    if (!timeSpan) return '09:00';
    if (typeof timeSpan === 'string') {
      return timeSpan.substring(0, 5); // Pega apenas HH:mm
    }
    return '09:00';
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData({
      ...formData,
      [name]: type === 'checkbox' ? checked : value,
    });
    
    // Limpa erro do campo quando ele é alterado
    if (errors[name]) {
      setErrors({ ...errors, [name]: null });
    }
  };

  const handlePlaceServiceToggle = (serviceId) => {
    setFormData(prev => ({
      ...prev,
      // IMPORTANTE: usar serviceId (base service ID), não placeServiceId
      serviceIds: prev.serviceIds.includes(serviceId)
        ? prev.serviceIds.filter(id => id !== serviceId)
        : [...prev.serviceIds, serviceId]
    }));
    
    if (errors.placeServices) {
      setErrors({ ...errors, placeServices: null });
    }
  };

  const validateForm = () => {
    const newErrors = {};

    // Valida se pelo menos um dia da semana foi selecionado
    const hasAtLeastOneDay = formData.monday || formData.tuesday || formData.wednesday || 
                             formData.thursday || formData.friday || formData.saturday || 
                             formData.sunday;
    
    if (!hasAtLeastOneDay) {
      newErrors.weekdays = 'Selecione pelo menos um dia da semana';
    }

    // Valida horários
    if (formData.startTime >= formData.endTime) {
      newErrors.endTime = 'Horário de fim deve ser posterior ao horário de início';
    }

    // Valida intervalo
    if (formData.breakBetweenAppointmentsMinutes < 0) {
      newErrors.breakBetweenAppointmentsMinutes = 'Intervalo não pode ser negativo';
    }

    // Valida PlaceServices (serviços oferecidos no local)
    if (formData.serviceIds.length === 0) {
      newErrors.placeServices = 'Selecione pelo menos um serviço oferecido no local que será atendido neste turno';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = () => {
    if (!validateForm()) {
      return;
    }

    // Converte horários para TimeSpan no formato esperado pela API
    const dataToSubmit = {
      ...formData,
      startTime: `${formData.startTime}:00`, // HH:mm:ss
      endTime: `${formData.endTime}:00`,
      breakBetweenAppointmentsMinutes: parseInt(formData.breakBetweenAppointmentsMinutes),
    };

    onSubmit(dataToSubmit);
  };

  const handleDelete = () => {
    if (window.confirm('Tem certeza que deseja excluir este turno?')) {
      onDelete(shift.id);
    }
  };

  const getSelectedDays = () => {
    const days = [];
    if (formData.monday) days.push('Seg');
    if (formData.tuesday) days.push('Ter');
    if (formData.wednesday) days.push('Qua');
    if (formData.thursday) days.push('Qui');
    if (formData.friday) days.push('Sex');
    if (formData.saturday) days.push('Sáb');
    if (formData.sunday) days.push('Dom');
    return days.join(', ');
  };

  const getSelectedPlaceServices = () => {
    return availablePlaceServices
      .filter(ps => formData.serviceIds.includes(ps.serviceId))
      .map(ps => ps.serviceName)
      .join(', ');
  };

  const getMinPlaceServiceDuration = () => {
    const selectedPlaceServices = availablePlaceServices.filter(ps => formData.serviceIds.includes(ps.serviceId));
    if (selectedPlaceServices.length === 0) return null;
    return Math.min(...selectedPlaceServices.map(ps => ps.durationMinutes || 30));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {shift?.id ? 'Editar Turno' : 'Novo Turno'}
      </DialogTitle>
      <DialogContent>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3, mt: 2 }}>
          {/* Seleção de dias da semana */}
          <Box>
            <Typography variant="subtitle2" gutterBottom fontWeight="medium">
              Dias da Semana *
            </Typography>
            <FormGroup row>
              <FormControlLabel
                control={
                  <Checkbox
                    name="monday"
                    checked={formData.monday}
                    onChange={handleChange}
                  />
                }
                label="Segunda"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="tuesday"
                    checked={formData.tuesday}
                    onChange={handleChange}
                  />
                }
                label="Terça"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="wednesday"
                    checked={formData.wednesday}
                    onChange={handleChange}
                  />
                }
                label="Quarta"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="thursday"
                    checked={formData.thursday}
                    onChange={handleChange}
                  />
                }
                label="Quinta"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="friday"
                    checked={formData.friday}
                    onChange={handleChange}
                  />
                }
                label="Sexta"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="saturday"
                    checked={formData.saturday}
                    onChange={handleChange}
                  />
                }
                label="Sábado"
              />
              <FormControlLabel
                control={
                  <Checkbox
                    name="sunday"
                    checked={formData.sunday}
                    onChange={handleChange}
                  />
                }
                label="Domingo"
              />
            </FormGroup>
            {errors.weekdays && (
              <Typography variant="caption" color="error">
                {errors.weekdays}
              </Typography>
            )}
          </Box>

          {/* Horários */}
          <Grid container spacing={2}>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TimeField
                label="Horário de Início"
                value={formData.startTime}
                onChange={(newTime) => {
                  setFormData({ ...formData, startTime: newTime });
                  if (errors.startTime) {
                    setErrors({ ...errors, startTime: null });
                  }
                }}
                error={!!errors.startTime}
                helperText={errors.startTime || 'Formato 24h (00:00 - 23:59)'}
                required
              />
            </Grid>
            <Grid size={{ xs: 12, sm: 6 }}>
              <TimeField
                label="Horário de Fim"
                value={formData.endTime}
                onChange={(newTime) => {
                  setFormData({ ...formData, endTime: newTime });
                  if (errors.endTime) {
                    setErrors({ ...errors, endTime: null });
                  }
                }}
                error={!!errors.endTime}
                helperText={errors.endTime || 'Formato 24h (00:00 - 23:59)'}
                required
              />
            </Grid>
          </Grid>

          {/* Intervalo */}
          <TextField
            fullWidth
            label="Intervalo entre Consultas (minutos)"
            name="breakBetweenAppointmentsMinutes"
            type="number"
            value={formData.breakBetweenAppointmentsMinutes}
            onChange={handleChange}
            inputProps={{ min: 0, step: 5 }}
            error={!!errors.breakBetweenAppointmentsMinutes}
            helperText={errors.breakBetweenAppointmentsMinutes || 'Tempo de descanso entre um atendimento e outro (ex: 0, 10, 15 minutos)'}
          />

          <Divider />

          {/* Seleção de Serviços do Local (PlaceServices) */}
          <Box>
            <Typography variant="subtitle2" gutterBottom fontWeight="medium">
              Serviços Oferecidos no Local (Atendidos neste Turno) *
            </Typography>
            <Typography variant="caption" color="text.secondary" gutterBottom display="block">
              Selecione quais serviços oferecidos no local você atende neste turno. Os slots serão gerados com base no menor serviço selecionado.
            </Typography>
            
            {availablePlaceServices.length === 0 ? (
              <Alert severity="warning" sx={{ mt: 1 }}>
                Nenhum serviço oferecido no local. Configure os serviços do local primeiro na aba de Locais.
              </Alert>
            ) : (
              <FormGroup sx={{ mt: 1 }}>
                {availablePlaceServices.map((placeService) => (
                  <FormControlLabel
                    key={placeService.id}
                    control={
                      <Checkbox
                        checked={formData.serviceIds.includes(placeService.serviceId)}
                        onChange={() => handlePlaceServiceToggle(placeService.serviceId)}
                        aria-label={`Selecionar serviço ${placeService.serviceName}`}
                      />
                    }
                    label={
                      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.5 }}>
                        <Typography>{placeService.serviceName}</Typography>
                        <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                          <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                            <Typography variant="caption" color="text.secondary">Duração:</Typography>
                            <Chip
                              label={`${placeService.durationMinutes || 30} min`}
                              size="small"
                              variant="outlined"
                            />
                          </Box>
                          {placeService.price !== null && placeService.price !== undefined && (
                            <Box sx={{ display: 'flex', gap: 0.5, alignItems: 'center' }}>
                              <Typography variant="caption" color="text.secondary">Preço:</Typography>
                              <Chip
                                label={`R$ ${placeService.price.toFixed(2)}`}
                                size="small"
                                color="primary"
                                variant="outlined"
                              />
                            </Box>
                          )}
                        </Box>
                      </Box>
                    }
                  />
                ))}
              </FormGroup>
            )}
            {errors.placeServices && (
              <Typography variant="caption" color="error" display="block" sx={{ mt: 1 }}>
                {errors.placeServices}
              </Typography>
            )}
          </Box>

          {/* Status ativo */}
          {shift && (
            <FormControlLabel
              control={
                <Checkbox
                  name="isActive"
                  checked={formData.isActive}
                  onChange={handleChange}
                />
              }
              label="Turno ativo"
            />
          )}

          {/* Resumo */}
          <Alert severity="info">
            <Typography variant="body2">
              <strong>Resumo:</strong><br />
              <strong>Dias:</strong> {getSelectedDays() || 'Nenhum dia selecionado'}<br />
              <strong>Horário:</strong> {formData.startTime} às {formData.endTime}<br />
              <strong>Serviços:</strong> {getSelectedPlaceServices() || 'Nenhum serviço selecionado'}
              {formData.serviceIds.length > 0 && (
                <>
                  <br />
                  <strong>Duração dos slots:</strong> {getMinPlaceServiceDuration()} minutos (baseado no menor serviço)
                </>
              )}
              {formData.breakBetweenAppointmentsMinutes > 0 && (
                <>
                  <br />
                  <strong>Intervalo:</strong> {formData.breakBetweenAppointmentsMinutes} minutos entre consultas
                </>
              )}
            </Typography>
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: 'space-between', px: 3, pb: 2 }}>
        <Box>
          {shift && onDelete && (
            <Button
              startIcon={<DeleteIcon />}
              onClick={handleDelete}
              color="error"
              variant="outlined"
            >
              Excluir
            </Button>
          )}
        </Box>
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button onClick={onClose}>Cancelar</Button>
          <Button onClick={handleSubmit} variant="contained">
            {shift ? 'Salvar' : 'Criar'}
          </Button>
        </Box>
      </DialogActions>
    </Dialog>
  );
}

ShiftForm.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onSubmit: PropTypes.func.isRequired,
  onDelete: PropTypes.func,
  shift: PropTypes.object,
  scheduleId: PropTypes.string.isRequired,
  availablePlaceServices: PropTypes.arrayOf(PropTypes.object),
};
